/*
 * DSD Visibility Layer - p11dz143
 * Governance-only visibility contract for canonical social objects.
 * Defines who may see canonical posts, stories, events, universe objects, galaxy objects,
 * and future chat previews without enabling Firebase reads, Firebase writes, or realtime.
 */
(function(){
  'use strict';
  if (window.DSDVisibilityLayer && window.DSDVisibilityLayer.version) return;

  var VERSION = 'p11dz143-visibility-layer-v1';
  var OWNER = 'VisibilityLayer';
  var FIREBASE_GATE = 'disabled-visibility-governance-only';

  function claim(surface, meta){
    try {
      if (window.DSDRegistry && typeof window.DSDRegistry.claim === 'function') {
        window.DSDRegistry.claim(surface, OWNER, Object.assign({ version: VERSION }, meta || {}));
      }
    } catch(e) {}
  }

  function exists(name){ return !!window[name]; }

  var visibilityStates = {
    public: {
      key: 'public',
      label: 'Public',
      description: 'Eligible for public/member discovery surfaces subject to content rating and moderation.',
      requiresAuth: false,
      requiresMembership: false,
      requiresModerator: false,
      futureUniverseCompatible: true,
      futureGalaxyCompatible: true
    },
    members: {
      key: 'members',
      label: 'Members Only',
      description: 'Visible to authenticated DSD members only.',
      requiresAuth: true,
      requiresMembership: false,
      requiresModerator: false,
      futureUniverseCompatible: true,
      futureGalaxyCompatible: true
    },
    followers: {
      key: 'followers',
      label: 'Followers',
      description: 'Visible to followers/mutuals once relationship registry exists.',
      requiresAuth: true,
      requiresRelationship: 'future-relationship-registry',
      requiresMembership: false,
      requiresModerator: false,
      futureUniverseCompatible: true,
      futureGalaxyCompatible: false,
      activationGate: 'Requires RelationshipRegistry before enforcement.'
    },
    private: {
      key: 'private',
      label: 'Private',
      description: 'Visible only to owner and platform/admin moderation systems.',
      requiresAuth: true,
      ownerOnly: true,
      requiresMembership: false,
      requiresModerator: false,
      futureUniverseCompatible: false,
      futureGalaxyCompatible: false
    },
    draft: {
      key: 'draft',
      label: 'Draft',
      description: 'Unpublished canonical object; not eligible for feed/profile/discovery rendering.',
      requiresAuth: true,
      ownerOnly: true,
      renderable: false,
      futureUniverseCompatible: true,
      futureGalaxyCompatible: true
    },
    moderationOnly: {
      key: 'moderationOnly',
      label: 'Moderation Only',
      description: 'Hidden from user surfaces and visible only to authorized moderation/admin paths.',
      requiresAuth: true,
      requiresModerator: true,
      renderable: false,
      futureUniverseCompatible: true,
      futureGalaxyCompatible: true
    },
    universeScoped: {
      key: 'universeScoped',
      label: 'Universe Scoped',
      description: 'Future visibility: visible through universe membership/follow/discovery rules only.',
      requiresAuth: true,
      requiresUniverseScope: true,
      futureOnly: true,
      futureUniverseCompatible: true,
      futureGalaxyCompatible: true,
      activationGate: 'Requires Universe Contract + MembershipRegistry + GovernanceRegistry before enforcement.'
    },
    galaxyScoped: {
      key: 'galaxyScoped',
      label: 'Galaxy Scoped',
      description: 'Future visibility: visible through galaxy membership/role/approval rules only.',
      requiresAuth: true,
      requiresGalaxyScope: true,
      futureOnly: true,
      futureUniverseCompatible: true,
      futureGalaxyCompatible: true,
      activationGate: 'Requires Galaxy Contract + MembershipRegistry + GovernanceRegistry before enforcement.'
    }
  };

  var objectDefaults = {
    post: 'members',
    profile: 'members',
    comment: 'inherits-parent-post',
    reaction: 'inherits-parent-post',
    notification: 'recipientOnly',
    story: 'members',
    event: 'public',
    universe: 'public',
    galaxy: 'public',
    chatMessage: 'galaxyScoped'
  };

  var surfaceVisibilityRules = {
    globalFeed: ['public', 'members'],
    profileFeed: ['public', 'members', 'followers', 'private-owner-only'],
    discoverySurface: ['public', 'members'],
    notificationSurface: ['recipientOnly'],
    modalSurface: ['inherits-source-surface'],
    futureStoryRail: ['public', 'members', 'followers', 'universeScoped', 'galaxyScoped'],
    futureUniverseSurface: ['public', 'members', 'universeScoped'],
    futureGalaxySurface: ['public', 'members', 'universeScoped', 'galaxyScoped'],
    futureGalaxyChatSurface: ['galaxyScoped']
  };

  var moderationOverrides = {
    hidden: {
      renderable: false,
      visibleTo: ['owner', 'platform-admin', 'moderator-scope'],
      reason: 'manual moderation hidden state'
    },
    underReview: {
      renderable: false,
      visibleTo: ['owner', 'platform-admin', 'moderator-scope'],
      reason: 'pending moderation review'
    },
    removed: {
      renderable: false,
      visibleTo: ['platform-admin'],
      reason: 'removed content audit trail only'
    },
    locked: {
      renderable: true,
      interactionAllowed: false,
      visibleTo: ['eligible-viewers'],
      reason: 'visible but interaction disabled'
    }
  };

  var visibilityDoctrine = [
    'Visibility is metadata on canonical social objects, not a separate storage architecture.',
    'Visibility filters may decide eligibility for a surface; they may not own rendering or persistence.',
    'Profile, universe, and galaxy visibility are scopes applied to canonical objects.',
    'Moderation state overrides visibility eligibility.',
    'Private/draft/moderation-only objects must not enter public feed hydration.',
    'Future universe and galaxy visibility require membership and governance registries before enforcement.',
    'No Firebase reads, writes, or realtime listeners are enabled by this layer.'
  ];

  function normalizeVisibility(value, fallback){
    if (!value) return fallback || 'members';
    if (visibilityStates[value]) return value;
    if (value === 'recipientOnly' || value === 'inherits-parent-post' || value === 'inherits-source-surface') return value;
    return fallback || 'members';
  }

  function getDefaultVisibility(objectType){
    return objectDefaults[objectType] || 'members';
  }

  function moderationStateOf(object){
    if (!object) return 'none';
    return object.moderationState || object.reviewState || (object.moderation && object.moderation.state) || 'none';
  }

  function evaluateVisibility(object, context){
    object = object || {};
    context = context || {};
    var objectType = object.objectType || object.type || 'post';
    var surfaceKey = context.surfaceKey || object.surfaceKey || 'globalFeed';
    var defaultVisibility = getDefaultVisibility(objectType);
    var visibility = normalizeVisibility(object.visibility || object.visibilityState, defaultVisibility);
    var moderationState = moderationStateOf(object);
    var moderationOverride = moderationOverrides[moderationState];
    var allowedBySurface = (surfaceVisibilityRules[surfaceKey] || []).indexOf(visibility) !== -1;
    if (!allowedBySurface && visibility === 'private' && surfaceKey === 'profileFeed' && context.viewerIsOwner) allowedBySurface = true;
    if (!allowedBySurface && visibility === 'galaxyScoped' && surfaceKey === 'futureGalaxySurface') allowedBySurface = true;
    if (!allowedBySurface && visibility === 'universeScoped' && surfaceKey === 'futureUniverseSurface') allowedBySurface = true;
    var renderable = !!allowedBySurface;
    var reason = allowedBySurface ? 'visibility-surface-match' : 'visibility-not-eligible-for-surface';

    if (moderationOverride) {
      renderable = !!moderationOverride.renderable;
      reason = 'moderation-override:' + moderationState;
    }
    if (visibility === 'draft' || visibility === 'moderationOnly') {
      renderable = false;
      reason = 'non-renderable-visibility:' + visibility;
    }

    return {
      version: VERSION,
      objectType: objectType,
      objectId: object.postId || object.id || object.objectId || null,
      surfaceKey: surfaceKey,
      visibility: visibility,
      defaultVisibility: defaultVisibility,
      moderationState: moderationState,
      renderable: renderable,
      reason: reason,
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      enforcementMode: 'governance-contract-only'
    };
  }

  function getReport(reason){
    return {
      version: VERSION,
      reason: reason || 'manual-report',
      owner: OWNER,
      firebaseGate: FIREBASE_GATE,
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      doctrine: visibilityDoctrine,
      visibilityStates: visibilityStates,
      objectDefaults: objectDefaults,
      surfaceVisibilityRules: surfaceVisibilityRules,
      moderationOverrides: moderationOverrides,
      dependencyReadiness: {
        DSDRegistry: exists('DSDRegistry'),
        SurfaceRegistry: exists('DSDSurfaceRegistry'),
        CanonicalPostStore: exists('DSDCanonicalPostStore'),
        CanonicalFeedHydrator: exists('DSDCanonicalFeedHydrator'),
        ProfilePostScope: exists('DSDProfilePostScope'),
        FirebaseSocialSchema: exists('DSDFirebaseSocialSchema'),
        RealtimeOwnershipAudit: exists('DSDRealtimeOwnershipAudit'),
        firebaseReadsEnabled: false,
        firebaseWritesEnabled: false,
        realtimeEnabled: false
      },
      nextGate: 'p11dz144-moderation-metadata-foundation-or-controlled-read-only-post-activation-gate'
    };
  }

  function publish(reason){
    var report = getReport(reason || 'publish');
    claim('visibility-layer:doctrine', { doctrine: visibilityDoctrine, count: visibilityDoctrine.length });
    claim('visibility-layer:states', { states: visibilityStates, count: Object.keys(visibilityStates).length });
    claim('visibility-layer:object-defaults', objectDefaults);
    claim('visibility-layer:surface-rules', surfaceVisibilityRules);
    claim('visibility-layer:moderation-overrides', moderationOverrides);
    claim('visibility-layer:dependency-readiness', report.dependencyReadiness);
    claim('visibility-layer:firebase-firewall', {
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      gate: FIREBASE_GATE
    });
    try { window.dispatchEvent(new CustomEvent('dsd:visibility-layer-ready', { detail: report })); } catch(e) {}
    window.DSDVisibilityLayerReport = report;
    return report;
  }

  window.DSDVisibilityLayer = {
    version: VERSION,
    owner: OWNER,
    getReport: getReport,
    publish: publish,
    visibilityStates: visibilityStates,
    objectDefaults: objectDefaults,
    surfaceVisibilityRules: surfaceVisibilityRules,
    moderationOverrides: moderationOverrides,
    normalizeVisibility: normalizeVisibility,
    getDefaultVisibility: getDefaultVisibility,
    evaluateVisibility: evaluateVisibility
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function(){ publish('dom-ready'); }, { once: true });
  } else {
    publish('already-ready');
  }
})();
